import {OrderState} from "../../data/models/subscription-order.model";
import {Address} from "../../data/models/address.model";
import {AddressCode} from "../../data/models/address-code.model";
import {BonusType} from "../../data/models/bonus.model";
import {SubExtendedData} from "../../data/models/sub-extended-data.model";
import {HouseNo, HouseNoFormat} from "../../data/models/houseno/houseno";
import {Injectable, inject} from "@angular/core";
import {BaseDataService} from "../services/base-data.service";
import {resolveBonusAddress} from "./bonus-address-resolution";
import {transformHouseNo} from "./address-utils";
import {UserData} from "../../data/models/user-data.model";

@Injectable({providedIn: 'root'})
export class SubscriptionOrderConverter {
  public static dateFormat = 'yyyy-MM-dd';

  private readonly baseDataService = inject(BaseDataService);

  convert(orderState: OrderState) {
    const subscriptionOrder: any = {}

    subscriptionOrder.offerId = orderState.offer?.recno;
    subscriptionOrder.orderChoiceKey = orderState.offer?.orderChoiceKey;
    subscriptionOrder.userBackendId = orderState.userBackendId;
    subscriptionOrder.quantity = 1;

    subscriptionOrder.validDate = {
      validFrom: orderState.payment.startdate
    };

    let birthday = orderState.addresses?.deliveryAddress?.userData?.birthDay;
    if (!birthday && birthday === '') {
      birthday = orderState.addresses?.billingAddress?.userData?.birthDay;
    }
    if (!birthday && birthday === '') {
      birthday = orderState.bonusData?.bonusAddress?.userData?.birthDay;
    }

    subscriptionOrder.birthday = birthday;

    let fullPhone = orderState.addresses?.deliveryAddress?.userData?.phone;
    if (!fullPhone && fullPhone === '') {
      fullPhone = orderState.addresses?.billingAddress?.userData?.phone;
    }
    if (!fullPhone && fullPhone === '') {
      fullPhone = orderState?.bonusData?.bonusAddress?.userData?.phone;
    }

    if (fullPhone) {
      const phoneSplit = fullPhone.split('/');
      if (phoneSplit.length == 2) {
        subscriptionOrder.phoneCode = phoneSplit[0];
        subscriptionOrder.phone = phoneSplit[1];
      }
    }

    let email = orderState.addresses?.deliveryAddress?.userData?.email;
    if (!email || email === '') {
      email = orderState.addresses?.billingAddress?.userData?.email;
    }

    if (!email || email === '') {
      email = orderState.registration?.email;
    }

    if (!email || email === '') {
      email = orderState.addresses?.email;
    }

    if (!email || email === '') {
      email = orderState.ssoEmail;
    }

    subscriptionOrder.email = email;

    const requiredAddresses = orderState.offer.requiredAddresses;
    subscriptionOrder.deliveryAddress = this.convertFullAddressData(requiredAddresses.deliveryAddressRequired || !SubscriptionOrderConverter.isAddressEmpty(orderState.addresses?.deliveryAddress), orderState.addresses?.deliveryAddress,
      subscriptionOrder.validDate.validFrom, null,
      false, subscriptionOrder.userBackendId);
    subscriptionOrder.billingAddress = this.convertFullAddressData(requiredAddresses.billingAddressRequired || !SubscriptionOrderConverter.isAddressEmpty(orderState.addresses?.billingAddress), orderState.addresses?.billingAddress,
      subscriptionOrder.validDate.validFrom, null,
      false, subscriptionOrder.userBackendId, subscriptionOrder.transaction?.clientAddress);
    SubscriptionOrderConverter.detachForeignBillingAddress(subscriptionOrder.billingAddress, orderState.userData, subscriptionOrder.deliveryAddress);

    subscriptionOrder.payment = SubscriptionOrderConverter.convertPaymentInfo(orderState.payment);

    //Für BonusType.NON_MONEY_BONUS && BonusType.THIRD_PARTY_NON_MONEY_BONUS sind Adressen notwendig
    if (orderState.bonusData?.bonus?.length > 0) {
      // Die Adressen gehören mit übergeben: Bei zugeklappter Prämienadress-Checkbox ist
      // bonusData.bonusAddress leer, und convertBonusData greift dann auf die Liefer- bzw.
      // Rechnungsadresse zurück (s. resolveBonusAddress).
      subscriptionOrder.selectedGiftList = SubscriptionOrderConverter.convertBonusData(
        orderState?.bonusData, orderState?.addresses, this.addressCodes()
      );
    }
    //Für THIRD_PARTY_NON_MONEY_BONUS (Werber) zusätzlich die Werberadresse setzen
    if (orderState.bonusData?.bonus?.[0]?.bonusType == BonusType.THIRD_PARTY_NON_MONEY_BONUS) {
      subscriptionOrder.advertiser = {
        person: SubscriptionOrderConverter.convertPerson(orderState.bonusData?.advertiserAddress, this.addressCodes()), //todo: ID (LWS) an der Person!
        address: SubscriptionOrderConverter.convertOnlyAddress(orderState.bonusData?.advertiserAddress)
      }
    }

    subscriptionOrder.variantCode = orderState.payment?.editionId;

    if (orderState.urlParams?.size > 0) {
      subscriptionOrder.additionalOfferParams = Object.fromEntries(orderState.urlParams);
    }

    subscriptionOrder.redirectUrl = orderState.redirectUrl;
    subscriptionOrder.userAgreement = orderState.userAgreement;

    if (orderState.additionalData) {
      let index = 0;
      let extendedData: SubExtendedData[] = [];
      orderState.additionalData.forEach(value => {
        let currentDataSet = orderState.additionalData[index];
        currentDataSet.value = value.value;
        let found = false;
        extendedData.forEach(data => {
          if(data.itemkey == currentDataSet.itemkey) {
            data.dataSets.push(currentDataSet);
            found = true;
          }
        });
        if(!found) {
          let newData = new SubExtendedData();
          newData.itemkey = orderState.additionalData[index].itemkey;
          newData.dataSets.push(currentDataSet);
          extendedData.push(newData);
        }
        index++;
      });
      subscriptionOrder.additionalData = extendedData;
    }

    if (orderState.optinList) {
      subscriptionOrder.optinList = [];
      orderState.optinList.forEach((checked: boolean, key: number)=> {
        if (checked) {
          subscriptionOrder.optinList.push(key);
        }
      });
    }

    if (orderState.transaction) {
      const transaction = orderState.transaction;
      subscriptionOrder.payment.onlinePayment = {};
      subscriptionOrder.payment.onlinePayment.transactionId = transaction.transactionId;
      subscriptionOrder.payment.onlinePayment.subscriptionId = transaction.subscriptionId;
      subscriptionOrder.payment.onlinePayment.transactionProvider = transaction.provider;
      subscriptionOrder.payment.onlinePayment.amount = transaction.amount;
      subscriptionOrder.payment.onlinePayment.transactionInfo = transaction.invoiceText;
      subscriptionOrder.payment.onlinePayment.authorizationId = transaction.authorizationId;
    }

    // Eine leere Rechnungsadresse wird durch die Lieferadresse ersetzt. Die Leerheit muss falsy geprüft
    // werden: bei zugeklapptem Block "abweichende Rechnungsadresse" steht in street nach einem reset()
    // null, bei deaktivierter Gruppe fehlt der address-Schlüssel ganz — beides ist kein '' (P5W-4409).
    if (!subscriptionOrder.billingAddress || SubscriptionOrderConverter.isAddressEmpty(subscriptionOrder.billingAddress) && subscriptionOrder.deliveryAddress) {
      subscriptionOrder.billingAddress = subscriptionOrder.deliveryAddress;
    } else if (subscriptionOrder.billingAddress && !subscriptionOrder.deliveryAddress || subscriptionOrder.deliveryAddress?.address.street == '') {
      subscriptionOrder.deliveryAddress = subscriptionOrder.billingAddress;
    }

    return subscriptionOrder;
  }

  /** Eine Adresse ohne Straße gilt als nicht angegeben — auch bei null/undefined, nicht nur bei ''. */
  private static isAddressEmpty(subscriptionAddress: { address?: { street?: string } }): boolean {
    return !subscriptionAddress?.address?.street;
  }

  static convertHouseNumber(addressFormJson: any, forView: boolean): string {
    // Country-Code immer in Großschrift normalisieren — Backend matcht 'A' case-sensitive, und
    // ein versehentlich kleinbuchstabiger Code würde sonst stillschweigend als DEFAULT behandelt.
    const rawCountry = typeof addressFormJson?.address?.country === "string"
        ? addressFormJson?.address?.country
        : addressFormJson?.address?.country?.key;
    const countryCode = (rawCountry ?? '').toUpperCase();

    const houseNumber = (addressFormJson?.address?.houseno ?? addressFormJson?.address?.houseNo ?? '') as string
    const houseNo = HouseNoFormat.ofCountryCode(countryCode) === HouseNoFormat.AUSTRIA
        ? HouseNo.ofAustriaParts(
            houseNumber,
            (addressFormJson?.address?.stiege as string) ?? '',
            (addressFormJson?.address?.stock  as string) ?? '',
            (addressFormJson?.address?.tuer   as string) ?? ''
          )
        : HouseNo.ofDefault(houseNumber)
    return forView ? HouseNo.formatForHuman(houseNo) : houseNo.houseNoVec;
  }

  convertFullAddressData(required: boolean, addressObject, validFrom: Date, validUntil: Date, forView: boolean, userBackendId: number, externalAddress?: Address): any {
    let subscriptionAddress = null;
    let initials = '';
    if (!addressObject?.person?.initials?.includes('.')) {
      addressObject?.person?.initials?.split('').forEach((letter: string) => initials = initials + letter.toUpperCase());
    } else {
      initials = addressObject?.person?.initials?.toUpperCase();
    }
    if (required) {
      const salutation = this.baseDataService.getBaseData<AddressCode>('addresscodes')
          .filter(sal => sal.key === addressObject?.person?.salutation);
      subscriptionAddress = {
        person: {
          firstname: addressObject?.person?.firstName ?? addressObject?.person?.firstname,
          lastname: addressObject?.person?.lastName ?? addressObject?.person?.lastname,
          email: addressObject?.userData?.email,
          title: SubscriptionOrderConverter.isString(addressObject?.person?.title) ? addressObject?.person?.title : (forView ? addressObject?.person?.title?.description : addressObject?.person?.title?.key),
          salutation: salutation && salutation.length > 0 ? forView ? salutation[0].description : salutation[0].defaultClientTypeKey : SubscriptionOrderConverter.isString(addressObject?.person?.salutation) ? addressObject?.person?.salutation : forView ? addressObject?.person?.salutation?.description : addressObject?.person?.salutation?.defaultClientTypeKey,
          initials: initials,
          middlename: addressObject?.person?.middlename
        },
        address: {
          street: addressObject?.address?.street,
          houseno: SubscriptionOrderConverter.convertHouseNumber(addressObject, forView),
          zipcode: addressObject?.address?.zipcode,
          city: addressObject?.address?.city,
          addrExtraLine1: addressObject?.address?.extraLine,
          // forView: Description für die Anzeige; sonst Key in Großschrift fürs Backend (case-sensitive Match auf 'A').
          country: SubscriptionOrderConverter.isString(addressObject?.address?.country)
              ? (forView ? addressObject?.address?.country : addressObject?.address?.country?.toUpperCase())
              : (forView ? addressObject?.address?.country?.description : addressObject?.address?.country?.key?.toUpperCase()),
          district: addressObject?.address?.district
        },
        validDate: {
          validFrom,
          validUntil
        },
        userBackendId: userBackendId ?? -1
      };
      //Wenn wir eine Adresse aus PayPal haben, und die Adresse aus dem Formular nicht vorhanden oder unvollständig ist,
      // überschreiben wir die Adresse mit der aus PayPal //todo: Adresse aus PayPal abfangen, sichern und hierher übergeben!!!
      if (
        externalAddress &&
        (!addressObject.address ||
          subscriptionAddress.address.street?.toString().length <= 0 ||
          subscriptionAddress.address.houseno?.toString().length <= 0 ||
          subscriptionAddress?.address.zipcode?.toString().length <= 0 ||
          subscriptionAddress.address.city?.toString().length <= 0 ||
          subscriptionAddress.address.country?.toString().length <= 0
        )
      ) {
        subscriptionAddress.address.street = externalAddress.street;
        subscriptionAddress.address.houseno = SubscriptionOrderConverter.convertHouseNumber(
          {address: {houseno: externalAddress.houseno, country: externalAddress.country}},
          forView
        );
        subscriptionAddress.address.zipcode = externalAddress.zipcode;
        subscriptionAddress.address.city = externalAddress.city;
        subscriptionAddress.address.addrExtraLine1 = null;

        // Konsistent zum normalen Pfad oben: Country-Code in Großschrift fürs Backend (case-sensitive 'A').
        subscriptionAddress.address.country = forView ? externalAddress.country : externalAddress.country?.toUpperCase();
        subscriptionAddress.address.district = null;
      }
    }

    return subscriptionAddress;
  }

  /**
   * Eine Rechnungsadresse, die nicht die Bestandsadresse des Kunden ist, gehört nicht zu seinem
   * Kundenkonto und geht deshalb ohne dessen Kundennummer (-1) raus - sonst lädt das Admintool sie
   * nicht richtig. Entscheidend ist der Inhalt, nicht ob die Maske aufgeklappt ist ('showAddress'):
   * Eine aufgeklappte, aber unverändert vorbefüllte Rechnungsadresse ist seine eigene, und bei
   * ONLY_BILLING ist die Maske immer offen, ohne dass 'showAddress' je gesetzt wird.
   *
   * Referenz ist die geladene Bestandsadresse; fehlt sie, die (für Bestandskunden readonly) Lieferadresse.
   */
  private static detachForeignBillingAddress(billingAddress: ComparableAddress, userData: UserData, deliveryAddress: ComparableAddress): void {
    if (!(billingAddress?.userBackendId > 0)) {
      return;
    }
    const customerAddress: ComparableAddress = userData?.address
      ? {person: userData.person, address: userData.address}
      : deliveryAddress;
    if (!SubscriptionOrderConverter.isSameAddress(billingAddress, customerAddress)) {
      billingAddress.userBackendId = -1;
    }
  }

  /** Gleiche Person unter gleicher Anschrift; Groß-/Kleinschreibung und Randleerzeichen zählen nicht. */
  private static isSameAddress(a: ComparableAddress, b: ComparableAddress): boolean {
    if (!a || !b) {
      return false;
    }
    const normalize = (value: string | null | undefined) => (value ?? '').toString().trim().toLowerCase();
    const sameField = (x: string | null | undefined, y: string | null | undefined) => normalize(x) === normalize(y);
    return sameField(a.person?.firstname, b.person?.firstname)
      && sameField(a.person?.lastname, b.person?.lastname)
      && sameField(a.address?.street, b.address?.street)
      && sameField(transformHouseNo(a.address?.houseno), transformHouseNo(b.address?.houseno))
      && sameField(a.address?.zipcode, b.address?.zipcode)
      && sameField(a.address?.city, b.address?.city)
      && sameField(a.address?.country, b.address?.country);
  }

  static isString(x): boolean {
    return Object.prototype.toString.call(x) === '[object String]';
  }

  static convertPaymentInfo(paymentInfos: any): any {
    let objBank: any;
    let payment: any = {};
    let paymentType: any;

    if (paymentInfos.iban) {
      objBank = {
        swiftIBAN: SubscriptionOrderConverter.isString(paymentInfos.iban) ? paymentInfos.iban?.trim() : paymentInfos.iban,
        accountHolder: paymentInfos.accountHolder?.trim()
      };
      payment.bank = objBank;
    }
    paymentType = {
      paymentMethod: paymentInfos.formOfPayment.paymentMethod,
      paymentFrequency: paymentInfos.paymentFrequency.key
    };
    payment.invoiceEmail = paymentInfos.invoiceEmail;
    payment.paymentType = paymentType;

    return payment;
  }

  private addressCodes(): AddressCode[] {
    return this.baseDataService.getBaseData<AddressCode>('addresscodes') ?? [];
  }

  static convertBonusData(bonusData, addresses?, addressCodes: AddressCode[] = []) {
    const selectedGiftList = [];
    if (bonusData && bonusData.bonus?.length > 0) {
      // Die Prämie geht an die abweichende Prämienadresse, sonst an die Liefer- bzw.
      // Rechnungsadresse. Bei zugeklappter Checkbox ist die bonusAddress-Gruppe deaktiviert
      // (P5W-4462) und damit leer - deshalb der Rückgriff auf die übrigen Adressen.
      const bonusAddress = resolveBonusAddress(bonusData.bonusAddress, addresses);

      //für jede Prämie Daten sammeln und in die Liste packen
      for (let bonus of bonusData.bonus) {
        let selectedGift: any = {};

        if (bonus.bonusType === BonusType.MONEY_BONUS || bonus.clientCoPayment > 0) {
          //Feldnamen müssen dem Backend-Modell Bank entsprechen, sonst verwirft das Mapping die Bankdaten still
          selectedGift.bankAccount = {
            swiftIBAN: bonusData.payment?.ibanBonus,
            swiftBIC: bonusData.payment?.bicBonus,
            accountHolder: bonusData.payment?.accountHolderBonus
          };
        }

        // Empfänger ist bei der Fremdprämie der Werber, sonst der Besteller.
        const giftAddress = bonus.bonusType === BonusType.THIRD_PARTY_NON_MONEY_BONUS
          ? bonusData.advertiserAddress
          : bonusAddress;

        if (giftAddress) {
          selectedGift.person = SubscriptionOrderConverter.convertPerson(giftAddress, addressCodes);
          selectedGift.address = SubscriptionOrderConverter.convertOnlyAddress(giftAddress);
        }
        selectedGift.quantity = 1;
        selectedGift.bonusType = bonus.bonusType;
        selectedGift.articleCode = bonus.articleCode;
        //kurzer Prämienname vor den Beschreibungstexten: der Wert landet an der Bestellung in der DB und wird dort ggf. abgeschnitten
        selectedGift.articleDescription = bonus.name || bonus.description || bonus.descriptionFromDocument;

        selectedGiftList.push(selectedGift);
      }
    }
    return selectedGiftList;
  }

  /**
   * Der Schlüssel eines Auswahlfeldes. Das Adressformular hält Anrede, Titel und Land mal als
   * reinen Schlüssel-String, mal als Objekt aus den Basisdaten - beide Formen müssen ankommen.
   * Ohne diese Unterscheidung ging der Wert bei der Formular-Schreibweise verloren (`.key` auf einem
   * String ist undefined) und ein leeres Feld brach die Bestellung mit einem TypeError ab.
   */
  private static keyOf(value: any): string {
    if (SubscriptionOrderConverter.isString(value)) {
      return value;
    }
    return value?.key ?? value?.defaultClientTypeKey;
  }

  /**
   * Das Formular hält die Anrede als Schlüssel der addresscodes, das Backend erwartet an der Person
   * aber den Kundentyp (defaultClientTypeKey) - wie bei Liefer- und Rechnungsadresse
   * (convertFullAddressData). Ohne die Abbildung landete der Anrede-Schlüssel in der WebDB, und
   * BaseDataService.handlePerson fand dazu keinen Kundentyp: die Anrede fehlte in der Anzeige.
   * Ein unbekannter Schlüssel geht unverändert raus.
   */
  private static toClientTypeKey(salutation: unknown, addressCodes: AddressCode[]): string {
    const clientTypeKey = (salutation as AddressCode)?.defaultClientTypeKey;
    if (!SubscriptionOrderConverter.isString(salutation) && clientTypeKey) {
      return clientTypeKey;
    }
    const key = SubscriptionOrderConverter.keyOf(salutation);
    return addressCodes.find(addressCode => addressCode.key === key)?.defaultClientTypeKey || key;
  }

  private static convertPerson(personData, addressCodes: AddressCode[]) {
    const person = personData?.person;
    return {
      firstname: person?.firstname ?? person?.firstName,
      lastname: person?.lastname ?? person?.lastName,
      title: SubscriptionOrderConverter.keyOf(person?.title),
      salutation: SubscriptionOrderConverter.toClientTypeKey(person?.salutation, addressCodes),
      initials: person?.initials,
      middlename: person?.middlename
    };
  }

  private static convertOnlyAddress(addressData) {
    const address = addressData?.address;
    // Country-Code in Großschrift - das Backend matcht 'A' case-sensitive (s. convertHouseNumber).
    const country = SubscriptionOrderConverter.keyOf(address?.country);
    return {
        country: country?.toUpperCase(),
        zipcode: address?.zipcode,
        city: address?.city,
        street: address?.street,
        houseno: SubscriptionOrderConverter.convertHouseNumber(addressData, false),
        addrExtraLine1: address?.extraLine,
        district: address?.district
    }
  }
}

/** Die für den Adressvergleich relevanten Felder einer konvertierten Adresse bzw. der Bestandsadresse. */
type ComparableAddress = {
  person?: { firstname?: string, lastname?: string },
  address?: { street?: string, houseno?: string, zipcode?: string, city?: string, country?: string },
  userBackendId?: number
};
